import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertTitle,
  AlertDescription,
  Separator,
  FormLabel,
} from '@roger/ui-components';
import { Upload, FileText, Image, X, Info, Loader2 } from 'lucide-react';
import { CODING_VISIT_TYPE_OPTIONS } from '../constants/codingVisitTypes';
import { client } from '../services/api';
import {
  runCodingDiagnosis,
  EMRSource,
  MedicalCodingOverwritePolicy,
  CodingVisitDataBase,
  WellskyOasisVisitType,
} from '../services/rogerDocApi';

const prevVisitTypes = [
  { value: 'SOC', label: 'SOC' },
  { value: 'ROC', label: 'ROC' },
  { value: 'REC', label: 'Recert' },
  { value: 'EVAL', label: 'Evaluation' },
  { value: 'REEVAL', label: 'Re-evaluation' },
];

interface UploadZoneProps {
  title: string;
  description: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  accept: string;
  icon: React.ReactNode;
}

function UploadZone({
  title,
  description,
  files,
  onFilesChange,
  accept,
  icon,
}: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      onFilesChange([...files, ...droppedFiles]);
    },
    [files, onFilesChange]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    onFilesChange([...files, ...selectedFiles]);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const inputId = `file-input-${title.replace(/\s/g, '-')}`;

  return (
    <div className='bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4 last:mb-0'>
      <div className='flex items-start gap-3.5 mb-4'>
        <div className='w-9 h-9 bg-gradient-to-br from-violet-400 to-violet-600 rounded-md flex items-center justify-center text-white flex-shrink-0'>
          {icon}
        </div>
        <div>
          <h3 className='text-sm font-semibold text-gray-900 mb-0.5'>
            {title}
          </h3>
          <p className='text-sm text-gray-500 leading-snug'>{description}</p>
        </div>
      </div>

      <div
        className={`border border-dashed rounded-md py-8 px-5 text-center cursor-pointer transition-all duration-200 bg-white
          ${
            isDragging
              ? 'border-violet-600 bg-violet-100 border-solid'
              : 'border-gray-300 hover:border-violet-600 hover:bg-violet-50'
          }`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <input
          type='file'
          id={inputId}
          multiple
          accept={accept}
          onChange={handleFileInput}
          className='hidden'
        />
        <div className='flex flex-col items-center gap-2.5'>
          <div className='w-12 h-12 bg-violet-100 rounded-md flex items-center justify-center text-violet-600'>
            <Upload className='w-7 h-7' />
          </div>
          <p className='text-sm text-gray-500'>
            Drag and drop your files here, or{' '}
            <span className='text-violet-600 font-medium'>browse</span>
          </p>
          <p className='text-xs text-gray-400'>Supports PDF, PNG, JPG, JPEG</p>
        </div>
      </div>

      {files.length > 0 && (
        <div className='mt-3 flex flex-col gap-1.5'>
          {files.map((file, index) => (
            <div
              key={index}
              className='flex items-center justify-between py-2.5 px-3 bg-white rounded border border-gray-200'
            >
              <div className='flex items-center gap-2.5'>
                <span className='text-violet-600 flex items-center'>
                  {file.type.includes('pdf') ? (
                    <FileText className='w-4 h-4' />
                  ) : (
                    <Image className='w-4 h-4' />
                  )}
                </span>
                <span className='text-sm text-gray-900 font-medium'>
                  {file.name}
                </span>
                <span className='text-xs text-gray-400'>
                  {(file.size / 1024).toFixed(1)} KB
                </span>
              </div>
              <button
                type='button'
                className='w-6 h-6 border-none bg-transparent rounded cursor-pointer flex items-center justify-center text-gray-400 transition-all duration-150 hover:bg-red-100 hover:text-red-600'
                onClick={e => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <X className='w-3.5 h-3.5' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface FormData {
  firstName: string;
  lastName: string;
  patientId: string;
  visitType: string;
  visitDate: string;
  episodeStartDate: string;
  prevVisitType: string;
  prevVisitDate: string;
  prevEpisodeStartDate: string;
}

interface NewVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisitCreated?: () => void;
}

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const NewVisitDialog: React.FC<NewVisitDialogProps> = ({
  open,
  onOpenChange,
  onVisitCreated,
}) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    patientId: '',
    visitType: 'SOC',
    visitDate: getTodayDate(),
    episodeStartDate: getTodayDate(),
    prevVisitType: '',
    prevVisitDate: '',
    prevEpisodeStartDate: '',
  });

  const [intakeFiles, setIntakeFiles] = useState<File[]>([]);
  const [visitFiles, setVisitFiles] = useState<File[]>([]);
  const [prevVisitFiles, setPrevVisitFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  const handleInputChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  const handleSelectChange = (field: keyof FormData) => (value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const isRecertification = formData.visitType.includes('REC');

  const isFormValid =
    formData.firstName &&
    formData.lastName &&
    formData.visitType &&
    formData.visitDate &&
    formData.episodeStartDate &&
    intakeFiles.length > 0 &&
    visitFiles.length > 0 &&
    (!isRecertification ||
      (prevVisitFiles.length > 0 &&
        formData.prevVisitType &&
        formData.prevVisitDate &&
        formData.prevEpisodeStartDate));

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      patientId: '',
      visitType: 'SOC',
      visitDate: getTodayDate(),
      episodeStartDate: getTodayDate(),
      prevVisitType: '',
      prevVisitDate: '',
      prevEpisodeStartDate: '',
    });
    setIntakeFiles([]);
    setVisitFiles([]);
    setPrevVisitFiles([]);
    setUploadError(null);
    setUploadProgress(null);
  };

  const handleSubmit = async () => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress('Preparing files...');

    try {
      // Collect all files (track counts for splitting later)
      const intakeCount = intakeFiles.length;
      const visitCount = visitFiles.length;
      const allFiles = [...intakeFiles, ...visitFiles, ...prevVisitFiles];

      if (allFiles.length === 0) {
        setUploadError('No files to upload');
        setIsUploading(false);
        return;
      }

      setUploadProgress(`Uploading ${allFiles.length} files...`);

      // Create FormData with all files
      const uploadFormData = new FormData();
      allFiles.forEach(file => {
        uploadFormData.append('files', file);
      });

      // Upload files to backend (backend handles S3 upload)
      const uploadResponse = await client.instance.post(
        '/rogerdoc/coding/upload-files',
        uploadFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!uploadResponse.data?.uploads) {
        throw new Error('Failed to upload files');
      }

      const { uploads } = uploadResponse.data;
      const s3Keys = uploads.map(
        (u: { s3_key: string; filename: string }) => u.s3_key
      );

      // Split s3_keys by document type based on original file order
      const intakeKeys = s3Keys.slice(0, intakeCount);
      const visitKeys = s3Keys.slice(intakeCount, intakeCount + visitCount);
      const prevVisitKeys = s3Keys.slice(intakeCount + visitCount);

      setUploadProgress('Processing documents...');

      // Generate patient ID if not provided
      const generatedPatientId = `auto${Math.floor(10000 + Math.random() * 90000)}`;
      const finalPatientId = formData.patientId.trim() || generatedPatientId;

      // Call full/diagnoses-generation endpoint from roger doc api
      const codingVisitData: CodingVisitDataBase = {
        patient_id: finalPatientId,
        visit_type: formData.visitType as WellskyOasisVisitType,
        visit_date_date: formData.visitDate,
        patient_first_name: formData.firstName,
        patient_last_name: formData.lastName,
        nurse_first_name: 'N/A',
        nurse_last_name: 'N/A',
        location: null,
      };

      const otherEMRSpecificData = {
        pdf_paths: {
          intake_documents: intakeKeys,
          visit_documents: visitKeys,
          previous_visit_documents: prevVisitKeys,
        },
        episode_start_date: formData.episodeStartDate || '',
      };

      const patientInfoResponse = await runCodingDiagnosis(
        EMRSource.OTHER,
        codingVisitData,
        MedicalCodingOverwritePolicy.NO_OVERWRITE,
        otherEMRSpecificData
      );

      setUploadProgress(
        'Processing started! It might take a few seconds to show in the list of patients.'
      );
      console.log('Coding (other emr) processing api response:', {
        patientInfoResponse,
      });

      resetForm();
      onOpenChange(false);
      onVisitCreated?.();
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Start new Roger AI Coding</DialogTitle>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Section 1: Patient & Visit Information */}
          <div>
            <div className='flex items-center gap-2.5 mb-5'>
              <span className='w-6 h-6 bg-violet-600 rounded flex items-center justify-center text-xs font-semibold text-white'>
                1
              </span>
              <h2 className='text-sm font-semibold text-gray-900 uppercase tracking-wide'>
                Patient & Visit Information
              </h2>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='flex flex-col gap-1.5'>
                <FormLabel htmlFor='firstName' required>
                  First Name
                </FormLabel>
                <Input
                  id='firstName'
                  placeholder='Enter first name'
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <FormLabel htmlFor='lastName' required>
                  Last Name
                </FormLabel>
                <Input
                  id='lastName'
                  placeholder='Enter last name'
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                />
              </div>

              <div className='flex flex-col gap-1.5 col-span-2'>
                <FormLabel htmlFor='patientId'>
                  Patient ID{' '}
                  <span className='text-gray-400 font-normal'>(Optional)</span>
                </FormLabel>
                <Input
                  id='patientId'
                  placeholder='Enter patient ID (optional)'
                  value={formData.patientId}
                  onChange={handleInputChange('patientId')}
                />
              </div>

              <div className='flex flex-col gap-1.5 col-span-2'>
                <FormLabel htmlFor='visitType' required>
                  Visit Type
                </FormLabel>
                <Select
                  value={formData.visitType}
                  onValueChange={handleSelectChange('visitType')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select visit type...' />
                  </SelectTrigger>
                  <SelectContent>
                    {CODING_VISIT_TYPE_OPTIONS.filter(
                      type =>
                        !type.value.startsWith('REC') &&
                        !type.value.startsWith('ROC')
                    ).map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                    {/* Note: REC and ROC visit types are not supported as of now */}
                  </SelectContent>
                </Select>
              </div>

              <div className='flex flex-col gap-1.5'>
                <FormLabel htmlFor='visitDate' required>
                  Visit Date
                </FormLabel>
                <Input
                  id='visitDate'
                  type='date'
                  value={formData.visitDate}
                  onChange={handleInputChange('visitDate')}
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <FormLabel htmlFor='episodeStartDate' required>
                  Episode Start Date
                </FormLabel>
                <Input
                  id='episodeStartDate'
                  type='date'
                  value={formData.episodeStartDate}
                  onChange={handleInputChange('episodeStartDate')}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Section 2: Document Uploads */}
          <div>
            <div className='flex items-center gap-2.5 mb-5'>
              <span className='w-6 h-6 bg-violet-600 rounded flex items-center justify-center text-xs font-semibold text-white'>
                2
              </span>
              <h2 className='text-sm font-semibold text-gray-900 uppercase tracking-wide'>
                Document Uploads
              </h2>
            </div>

            <UploadZone
              title='Intake Documents'
              description='Examples: Home Health Order/Referral, Face to Face visits, History and Physical, etc.'
              files={intakeFiles}
              onFilesChange={setIntakeFiles}
              accept='.pdf,.png,.jpg,.jpeg'
              icon={<FileText className='w-4 h-4' />}
            />

            <UploadZone
              title='Visit Documents'
              description='Examples: Exported OASIS form, Active Medication List, Wound Documentation, etc.'
              files={visitFiles}
              onFilesChange={setVisitFiles}
              accept='.pdf,.png,.jpg,.jpeg'
              icon={<FileText className='w-4 h-4' />}
            />

            {isRecertification && (
              <div className='animate-in slide-in-from-top-2 duration-250'>
                <Separator className='my-6' />

                <div className='flex items-center gap-2.5 mb-5'>
                  <span className='w-6 h-6 bg-violet-600 rounded flex items-center justify-center text-xs font-semibold text-white'>
                    3
                  </span>
                  <h2 className='text-sm font-semibold text-gray-900 uppercase tracking-wide'>
                    Previous Visit Information
                  </h2>
                </div>

                <div className='grid grid-cols-2 gap-4 mb-4'>
                  <div className='flex flex-col gap-1.5'>
                    <FormLabel htmlFor='prevVisitType' required>
                      Previous Visit Type
                    </FormLabel>
                    <Select
                      value={formData.prevVisitType}
                      onValueChange={handleSelectChange('prevVisitType')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select previous visit type...' />
                      </SelectTrigger>
                      <SelectContent>
                        {prevVisitTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='flex flex-col gap-1.5'>
                    <FormLabel htmlFor='prevVisitDate' required>
                      Previous Visit Date
                    </FormLabel>
                    <Input
                      id='prevVisitDate'
                      type='date'
                      value={formData.prevVisitDate}
                      onChange={handleInputChange('prevVisitDate')}
                    />
                  </div>

                  <div className='flex flex-col gap-1.5'>
                    <FormLabel htmlFor='prevEpisodeStartDate' required>
                      Previous Episode Start Date
                    </FormLabel>
                    <Input
                      id='prevEpisodeStartDate'
                      type='date'
                      value={formData.prevEpisodeStartDate}
                      onChange={handleInputChange('prevEpisodeStartDate')}
                    />
                  </div>
                </div>

                <UploadZone
                  title='Previous Visit Documents'
                  description='Upload documentation from the previous visit for recertification reference.'
                  files={prevVisitFiles}
                  onFilesChange={setPrevVisitFiles}
                  accept='.pdf,.png,.jpg,.jpeg'
                  icon={<FileText className='w-4 h-4' />}
                />
              </div>
            )}

            <Alert className='mt-6 bg-violet-50 border-violet-200'>
              <Info className='h-4 w-4 text-violet-600' />
              <AlertTitle className='text-violet-800 font-semibold'>
                Background Processing
              </AlertTitle>
              <AlertDescription className='text-violet-700'>
                After upload, documents will be automatically processed for text
                extraction and AI analysis in the background. You can view the
                processing status in the intake history.
              </AlertDescription>
            </Alert>
          </div>
        </div>

        {uploadError && (
          <Alert variant='destructive' className='mt-4'>
            <AlertDescription>{uploadError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {uploadProgress && isUploading && (
            <span className='text-sm text-gray-500 mr-auto'>
              {uploadProgress}
            </span>
          )}
          <Button
            variant='outline'
            onClick={handleCancel}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button disabled={!isFormValid || isUploading} onClick={handleSubmit}>
            {isUploading ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Uploading...
              </>
            ) : (
              'Upload Files'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewVisitDialog;
